import React, { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { 
  // Basic UI icons
  Shield, 
  Clock, 
  Users, 
  BarChart3, 
  Star, 
  Heart,
  CheckCircle,
  XCircle,
  TrendingUp,
  AlertTriangle,
  Activity,
  AlertCircle,
  Eye,
  Award // Performance award icon
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format, subDays } from 'date-fns';
import { useBaseScorePerformanceEvaluation } from '@/hooks/useBaseScorePerformanceEvaluation';
import { PerformanceSourceModal } from './PerformanceSourceModal';
import { useShiftaskEvaluation } from '@/hooks/useShiftaskEvaluation';
import { DistinctionBadge } from './DistinctionBadge';
import { PerformanceScoreExplanation } from './PerformanceScoreExplanation';
import { ShiftaskEvaluationModal } from './ShiftaskEvaluationModal';
import { ClientEvaluationModal } from './ClientEvaluationModal';

interface PerformanceBreakdownViewProps {
  guardId: string;
  guardName: string;
  teamId?: string;
}

interface PerformanceMetric {
  title: string;
  score: number;
  maxScore: number;
  icon: React.ReactNode;
  description: string;
  details: any;
  color: string;
}

export const PerformanceBreakdownView: React.FC<PerformanceBreakdownViewProps> = ({ 
  guardId, 
  guardName,
  teamId
}) => {
  const { t } = useLanguage();
  
  // Helper function to translate behavior names
  const translateBehaviorName = (name: string | undefined) => {
    if (!name) return '';
    
    // Define behavior name translations
    const behaviorTranslations: { [key: string]: string } = {
      'Απρεπής Συμπεριφορά': t('inappropriateBehavior') || 'Inappropriate Behavior',
      'Εγκατάλειψη Θέσης': t('postAbandonment') || 'Post Abandonment',
      'Καθυστέρηση': t('delay') || 'Delay',
      'Απουσία': t('absence') || 'Absence',
      'Θετική Αναφορά': t('positiveReport') || 'Positive Report',
      'Αρνητική Αναφορά': t('negativeReport') || 'Negative Report',
      'Έγκαιρη Άφιξη': t('timelyArrival') || 'Timely Arrival',
      'Παρουσία Εντός 15λεπτων': t('presenceWithin15min') || 'Presence Within 15min',
      'Συνέπεια': t('consistency') || 'Consistency',
      'Πρωτοβουλία': t('initiative') || 'Initiative',
      'Συνεργασία': t('cooperation') || 'Cooperation'
    };
    
    return behaviorTranslations[name] || name;
  };
  
  const [selectedMetric, setSelectedMetric] = useState<string | null>(null);
  const [showOvitModal, setShowOvitModal] = useState(false);
  const [showClientEvaluationModal, setShowClientEvaluationModal] = useState(false);
  const endDate = new Date();
  const startDate = subDays(endDate, 30); // Last 30 days

  // Χρήση του νέου συστήματος βασικού σκορ
  const { 
    data: performanceResult, 
    isLoading: performanceLoading,
    error: performanceError 
  } = useBaseScorePerformanceEvaluation(
    guardId,
    startDate.toISOString().split('T')[0],
    endDate.toISOString().split('T')[0],
    true // Enable real-time updates
  );

  console.log('Performance loading:', performanceLoading);
  console.log('Performance error:', performanceError);
  console.log('Performance result:', performanceResult);

  // Add detailed debugging for behavior data
  if (performanceResult) {
    console.log('=== BEHAVIOR DEBUG IN UI ===');
    console.log('Behavior score from performance:', performanceResult.behavior_score);
    console.log('Behavior points from performance:', performanceResult.behavior_points);
    console.log('Performance details object:', performanceResult.details);
    console.log('Details positive_behaviors:', performanceResult.details?.positive_behaviors);
    console.log('Details negative_behaviors:', performanceResult.details?.negative_behaviors);
    console.log('Details behavior_records_count:', performanceResult.details?.behavior_records_count);
    console.log('Behavior details from performance:', performanceResult.behavior_details);
    console.log('============================');
  }

  // Φόρτωση Shiftask evaluation
  const { data: shiftaskEvaluation } = useShiftaskEvaluation(guardId);

  // Fetch behavior records directly from the table
  const { data: directBehaviorRecords = [] } = useQuery({
    queryKey: ['direct-behavior-records', guardId],
    queryFn: async () => {
      console.log('=== DIRECT BEHAVIOR FETCH ===');
      const { data, error } = await supabase
        .from('behavior_records')
        .select(`
          *,
          behavior_type:behavior_types!behavior_type_id(*)
        `)
        .eq('guard_id', guardId)
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      console.log('Direct behavior fetch result:', { data, error, guardId });
      if (error) throw error;
      return data || [];
    },
    staleTime: 0, // Always fetch fresh
    gcTime: 0,    // No cache
  });

  // Calculate behavior counts directly from the fetched data
  const directPositiveCount = directBehaviorRecords.filter(r => r.behavior_type?.category === 'positive').length;
  const directNegativeCount = directBehaviorRecords.filter(r => r.behavior_type?.category === 'negative').length;
  const directTotalScore = directBehaviorRecords.reduce((sum, r) => sum + (r.score_applied || 0), 0);

  console.log('=== DIRECT BEHAVIOR CALCULATIONS ===');
  console.log('Direct behavior records:', directBehaviorRecords);
  console.log('Direct positive count:', directPositiveCount);
  console.log('Direct negative count:', directNegativeCount);
  console.log('Direct total score:', directTotalScore);
  console.log('====================================');

  // Fetch behavior records with proper date range (use semester dates instead of last 30 days)
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const currentSemester = currentMonth < 6 ? 'H1' : 'H2';
  const semesterStartDate = new Date(currentYear, currentSemester === 'H1' ? 0 : 6, 1);
  const semesterEndDate = new Date(currentYear, currentSemester === 'H1' ? 5 : 11, 31);
  
  const { data: behaviorRecords = [] } = useQuery({
    queryKey: ['performance-behavior', guardId, currentSemester, currentYear],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('behavior_records')
        .select(`
          *,
          behavior_type:behavior_types!behavior_type_id(*)
        `)
        .eq('guard_id', guardId)
        .gte('incident_date', semesterStartDate.toISOString().split('T')[0])
        .lte('incident_date', semesterEndDate.toISOString().split('T')[0])
        .eq('status', 'active');

      console.log('Behavior records query result:', { data, error, guardId, semesterStartDate, semesterEndDate });
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch clock records
  const { data: clockRecords = [] } = useQuery({
    queryKey: ['performance-clock', guardId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clock_in_out')
        .select('*')
        .eq('user_id', guardId)
        .gte('clock_in', startDate.toISOString())
        .order('clock_in', { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });

  // Fetch client evaluations
  const { data: clientEvaluations = [] } = useQuery({
    queryKey: ['performance-client-evaluations', guardId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('client_evaluations')
        .select('*')
        .eq('guard_id', guardId)
        .gte('submitted_at', startDate.toISOString())
        .order('submitted_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });

  // Fetch patrol logs (from guard_locations or similar)
  const { data: patrols = [] } = useQuery({
    queryKey: ['performance-patrols', guardId],
    queryFn: async () => {
      // Since patrol_logs doesn't exist, we'll use guard_locations as proxy
      const { data, error } = await supabase
        .from('guard_locations')
        .select('*')
        .eq('guard_id', guardId)
        .gte('created_at', startDate.toISOString())
        .eq('tracking_type', 'patrol')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });

  // Fetch patrol observations for accurate scoring
  const { data: patrolObservations = [] } = useQuery({
    queryKey: ['performance-patrol-observations', guardId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('patrol_observations')
        .select('*')
        .eq('guard_id', guardId)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });

  // Fetch emergency reports
  const { data: emergencyReports = [] } = useQuery({
    queryKey: ['performance-emergency-reports', guardId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emergency_reports')
        .select('*')
        .eq('guard_id', guardId)
        .gte('created_at', semesterStartDate.toISOString())
        .lte('created_at', semesterEndDate.toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });

  // Fetch supervisor reports
  const { data: supervisorReports = [] } = useQuery({
    queryKey: ['performance-supervisor-reports', guardId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('supervisor_reports')
        .select('*')
        .eq('guard_id', guardId)
        .gte('created_at', semesterStartDate.toISOString())
        .lte('created_at', semesterEndDate.toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });
  if (performanceLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-2">{t("loadingPerformanceData") || "Loading performance data..."}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (performanceError) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-red-600">
            <AlertCircle className="h-8 w-8 mx-auto mb-2" />
            <p>Σφάλμα φόρτωσης δεδομένων: {performanceError.message}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Use direct data instead of performance result for behavior counts
  const positiveBehs = directPositiveCount;
  const negativeBehs = directNegativeCount;
  
  console.log('=== UI BEHAVIOR VALUES (DIRECT) ===');
  console.log('positiveBehs (direct):', positiveBehs);
  console.log('negativeBehs (direct):', negativeBehs);
  console.log('directTotalScore:', directTotalScore);
  console.log('actualBehaviorPointsImpact (fallback):', performanceResult?.behavior_points || 0);
  console.log('===================================');
  
  // Calculate correct behavior points impact from performance result
  const actualBehaviorPointsImpact = performanceResult?.behavior_points || 0;

  // Δημιουργία metrics από το νέο σύστημα
  const createMetricsFromPerformance = () => {
    if (!performanceResult) return [];

    // Use direct behavior data instead of performance calculation
    const positiveBehs = directPositiveCount;
    const negativeBehs = directNegativeCount;

    // Add comprehensive null safety checks for details object and all its properties
    const details = performanceResult.details || {};
    const safePatrolActivities = details.patrol_activities ?? 0;
    const safeEmergencyReports = details.emergency_reports ?? 0;
    const safeSupervisorReportsCount = supervisorReports.length ?? 0;
    const safeClientEvaluationsCount = details.client_evaluations_count ?? 0;
    const safeBehaviorRecordsCount = details.behavior_records_count ?? 0;
    const safeTotalShifts = details.total_shifts ?? 0;
    const safePatrolCompletionRate = details.patrol_completion_rate ?? 0;
    const safePatrolPerformanceMessage = details.patrol_performance_message ?? '';

    // Use the same data as the RPC function - no local calculations
    const actualPatrolObservationPoints = performanceResult.patrol_observations_points || 0;
    const actualPatrolObservationCount = performanceResult?.details?.patrol_observations_count || 0;

    return [
      {
        title: t("operationalActivity") || "Operational Activity",
        score: performanceResult.operational_activity_score || 0,
        maxScore: 10,
        icon: <Activity className="h-4 w-4" />,
        description: `${performanceResult?.operational_activity_details?.emergency_reports || 0} emergency reports × 2 + ${performanceResult?.operational_activity_details?.patrol_observations || 0} observations × 1`,
        details: {
          emergency_reports: performanceResult?.operational_activity_details?.emergency_reports || 0,
          patrol_observations_count: performanceResult?.operational_activity_details?.patrol_observations || 0,
          emergency_points: (performanceResult?.operational_activity_details?.emergency_reports || 0) * 2,
          patrol_observations_points: (performanceResult?.operational_activity_details?.patrol_observations || 0) * 1,
          total_operational_score: performanceResult.operational_activity_score || 0,
        },
        color: (performanceResult.operational_activity_score || 0) > 0 ? "text-green-600 dark:text-green-400" : "text-muted-foreground"
      },
      {
        title: t("supervisorReports") || "Supervisor Reports",
        score: performanceResult.supervisor_reports_points || 0,
        maxScore: 10,
        icon: <Users className="h-4 w-4" />,
        description: `${safeSupervisorReportsCount} ${t("supervisorReportsCount") || "supervisor reports"}`,
        details: {
          supervisor_reports_count: safeSupervisorReportsCount,
          points_impact: performanceResult.supervisor_reports_points || 0,
        },
        color: (performanceResult.supervisor_reports_points || 0) !== 0 ? 
               (performanceResult.supervisor_reports_points || 0) > 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400" 
               : "text-muted-foreground"
      },
      {
        title: t("clientEvaluations") || "Client Evaluations",
        score: Math.max(0, Math.min(5, performanceResult.client_evaluation_points || 0)),
        maxScore: 5,
        icon: <Star className="h-4 w-4" />,
        description: `${safeClientEvaluationsCount} ${t("clientEvaluationsCount") || "client evaluations"}`,
        details: {
          client_evaluations_count: performanceResult?.client_evaluation_details?.evaluation_count || 0,
          points_impact: performanceResult.client_evaluation_points || 0,
        },
        color: "text-yellow-600 dark:text-yellow-400"
      },
      {
        title: t("behaviors") || "Behaviors",
        score: directTotalScore, // Use direct calculation
        maxScore: 15,
        icon: <Heart className="h-4 w-4" />,
        description: `${positiveBehs} ${t("positive") || "positive"}, ${negativeBehs} ${t("negative") || "negative"}`,
        details: {
          positive_behaviors: directPositiveCount,
          negative_behaviors: directNegativeCount,
          behavior_records_count: directBehaviorRecords.length,
          points_impact: directTotalScore,
        },
        color: directTotalScore >= 0 ? "text-purple-600 dark:text-purple-400" : "text-red-600 dark:text-red-400"
      },
      {
        title: t("punctualityAndTimely") || "Punctuality & Timely Check-in",
        score: Math.max(0, Math.min(5, performanceResult.punctuality_points || 0)),
        maxScore: 5,
        icon: <Clock className="h-4 w-4" />,
        description: `${safeTotalShifts} ${t("shiftsTotal") || "shifts total"}`,
        details: {
          total_shifts: performanceResult?.punctuality_details?.total_shifts || 0,
          punctuality_points: performanceResult.punctuality_points || 0,
        },
        color: (performanceResult.punctuality_points || 0) >= 0 ? "text-green-600 dark:text-green-400" : "text-orange-600 dark:text-orange-400"
      }
    ];
  };

  const metrics = createMetricsFromPerformance();
  
  // Add metricType to each metric for easier identification
  const metricsWithTypes = metrics.map((metric, index) => ({
    ...metric,
    metricType: ['operational', 'supervisor', 'client', 'behavior', 'punctuality'][index]
  }));
  
  // Use the same calculation as AdminPerformanceInterface - no local corrections
  const totalScore = performanceResult?.final_score || 0;
  const baseScore = performanceResult?.base_score || 0; // Updated to reflect OVIT-based scoring
  const percentage = Math.min(100, (totalScore / 100) * 100); // Max score is 100, but cap display at 100%
  
  // Handle distinction display for scores over 100
  const displayScore = totalScore > 100 ? "100+" : totalScore.toString();
  const hasDistinction = totalScore > 100;

  const getGrade = (score: number) => {
    if (score >= 95) return 'A+';
    if (score >= 90) return 'A';
    if (score >= 85) return 'B+';
    if (score >= 80) return 'B';
    if (score >= 75) return 'C+';
    if (score >= 70) return 'C';
    if (score >= 60) return 'D';
    return 'F';
  };

  const getGradeColor = (grade: string) => {
    switch (grade) {
      case 'A+':
      case 'A':
        return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-800';
      case 'B+':
      case 'B':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800';
      case 'C+':
      case 'C':
        return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800';
      case 'D':
        return 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 border-orange-200 dark:border-orange-800';
      case 'F':
        return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-800';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const grade = performanceResult?.performance_grade || getGrade(totalScore);

  if (performanceLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (performanceError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
            <AlertCircle className="h-5 w-5" />
            Σφάλμα Υπολογισμού
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {t("couldNotCalculatePerformance") || "Could not calculate performance. Please try again."}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!performanceResult) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-orange-600 dark:text-orange-400">
            <AlertTriangle className="h-5 w-5" />
            {t("noDataAvailable") || "No Data Available"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {t("notEnoughData") || "Not enough data to calculate performance for the selected period."}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Performance Summary - Νέο Σύστημα */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl">{guardName}</CardTitle>
              <CardDescription>
                {t("hundredPointSystem") || "100-Point Performance Evaluation System"} - 
                {shiftaskEvaluation ? 
                  ` ${t("shiftaskBase") || "Shiftask Base"}: ${(shiftaskEvaluation.overall_score * 0.8).toFixed(0)} ${t("points") || "points"}` : 
                  ` ${t("base") || "Base"}: ${performanceResult?.base_score || 0} ${t("points") || "points"} ${performanceResult?.base_score === 0 ? `(${t("withoutEvaluation") || "Without Evaluation"})` : ''}`
                }
                <br />
                ({format(startDate, 'dd/MM/yyyy')} - {format(endDate, 'dd/MM/yyyy')})
              </CardDescription>
            </div>
            <div className="flex flex-col items-end gap-2">
              <Button 
                onClick={() => setShowClientEvaluationModal(true)}
                variant="outline"
                size="sm"
              >
                <Star className="h-4 w-4 mr-2" />
                {t("companyEvaluation") || "Company Evaluation"}
              </Button>
              <div className="text-right">
                <div className="text-3xl font-bold mb-2">
                  {totalScore > 100 ? '100+' : totalScore.toFixed(1)}
                </div>
                <Badge className={`${getGradeColor(grade)} text-sm px-3 py-1 mb-2`}>
                  {totalScore > 100 ? 'A+' : grade}
                </Badge>
                {totalScore > 100 && (
                  <div className="mt-2">
                    <DistinctionBadge score={totalScore} size="sm" />
                  </div>
                )}
                <div className="text-xs text-muted-foreground">
                  Βάση: {baseScore} + Επιπλέον: {(totalScore - baseScore).toFixed(1)} = {totalScore.toFixed(1)}
                </div>
                {shiftaskEvaluation && (
                  <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    ✓ {t("hasShiftaskEvaluation") || "Has Shiftask Evaluation"}
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span>{t("totalScore") || "Total Score"}</span>
              <span>
                {totalScore > 100 ? '100+' : totalScore.toFixed(1)} / 100
                {totalScore > 100 && (
                  <span className="text-amber-600 ml-1">(Actual: {totalScore.toFixed(1)})</span>
                )}
              </span>
            </div>
            <Progress value={percentage} className="h-3" />
            <div className="grid grid-cols-2 gap-4 text-xs">
              <div className="flex justify-between">
                <span>{t("baseScore") || "Base Score"}:</span>
                <span className="font-medium">{baseScore}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("additionalPoints") || "Additional Points"}:</span>
                <span className={`font-medium ${(totalScore - baseScore) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {(totalScore - baseScore) >= 0 ? '+' : ''}{(totalScore - baseScore).toFixed(1)}
                </span>
              </div>
            </div>
            {shiftaskEvaluation && (
              <div className="mt-3 p-3 bg-blue-50/80 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="text-xs font-medium text-blue-800 dark:text-blue-300 mb-1">
                  {t("shiftaskEvaluation") || "Shiftask Evaluation"} ({format(new Date(shiftaskEvaluation.evaluation_date), 'dd/MM/yyyy')})
                </div>
                <div className="text-xs text-blue-600 dark:text-blue-400">
                  {t("baseScore") || "Base Score"}: {(shiftaskEvaluation.overall_score * 0.8).toFixed(0)} {t("points") || "points"} ({t("from") || "from"} {shiftaskEvaluation.overall_score}% Shiftask) • 
                  {t("status") || "Status"}: {shiftaskEvaluation.status === 'active' ? t("active") || "Active" : shiftaskEvaluation.status}
                </div>
                {shiftaskEvaluation.notes && (
                  <div className="text-xs text-muted-foreground mt-1 italic">
                    {shiftaskEvaluation.notes}
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Performance Metrics Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {metricsWithTypes.map((metric, index) => {
          const metricPercentage = metric.maxScore > 0 ? (metric.score / metric.maxScore) * 100 : 0;
          
          return (
            <Card key={index} className="cursor-pointer hover:shadow-none">
              <CardHeader className="pb-3">
                <CardTitle className={`flex items-center justify-between text-sm ${metric.color}`}>
                  <div className="flex items-center gap-2">
                    {metric.icon}
                    {metric.title}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (metric.metricType === 'ovit') {
                        setShowOvitModal(true);
                      } else {
                        setSelectedMetric(metric.metricType);
                      }
                    }}
                    className="h-6 w-6 p-0"
                  >
                    <Eye className="h-3 w-3" />
                  </Button>
                </CardTitle>
                <CardDescription className="text-xs">{metric.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xl font-bold text-foreground">{metric.score.toFixed(1)}</span>
                    <span className="text-sm text-muted-foreground">/ {metric.maxScore}</span>
                  </div>
                  <Progress value={metricPercentage} className="h-2" />
                  <div className="text-xs text-muted-foreground">
                    {metricPercentage.toFixed(1)}% {t("performance") || "performance"}
                  </div>
                  {metric.details && (
                    <div className="text-xs space-y-1 pt-2 border-t border-border">
                      {Object.entries(metric.details).map(([key, value]) => (
                        <div key={key} className="flex justify-between">
                          <span className="capitalize text-muted-foreground">
                            {key.replace(/_/g, ' ').replace('avg', 'Μέσος όρος').replace('total', 'Σύνολο')}:
                          </span>
                          <span className="font-medium text-foreground">{String(value)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Score Explanation */}
      <PerformanceScoreExplanation />

      {/* Shiftask Evaluation Modal */}
      <ShiftaskEvaluationModal
        guardId={guardId}
        guardName={guardName}
        isOpen={showOvitModal}
        onClose={() => setShowOvitModal(false)}
      />

      {/* Client Evaluation Modal */}
      <ClientEvaluationModal
        open={showClientEvaluationModal}
        onOpenChange={setShowClientEvaluationModal}
        guardId={guardId}
        guardName={guardName}
        teamId={teamId}
      />

      {/* Recent Behavior Records - Use Direct Data */}
      {directBehaviorRecords.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-700 dark:text-green-400">
                <CheckCircle className="h-5 w-5" />
                {t("positiveBehaviors") || "Positive Behaviors"} ({directPositiveCount})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {directPositiveCount === 0 ? (
                <p className="text-muted-foreground text-center py-4">{t("noPositiveBehaviors") || "No positive behaviors"}</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {directBehaviorRecords.filter(r => r.behavior_type?.category === 'positive').slice(0, 5).map((record) => (
                    <div key={record.id} className="flex items-center justify-between p-2 bg-green-50/80 dark:bg-green-900/20 rounded-lg">
                      <div>
                        <div className="font-medium text-sm text-foreground">{translateBehaviorName(record.behavior_type?.name)}</div>
                        <div className="text-xs text-muted-foreground">
                          {format(new Date(record.incident_date), 'dd/MM/yyyy')}
                        </div>
                      </div>
                      <div className="text-green-700 dark:text-green-400 font-bold text-sm">
                        +{record.score_applied}
                      </div>
                    </div>
                  ))}
                  {directPositiveCount > 5 && (
                    <div className="text-xs text-muted-foreground text-center">
                      και {directPositiveCount - 5} ακόμα...
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-400">
                <XCircle className="h-5 w-5" />
                {t("negativeBehaviors") || "Negative Behaviors"} ({directNegativeCount})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {directNegativeCount === 0 ? (
                <p className="text-muted-foreground text-center py-4">{t("noNegativeBehaviors") || "No negative behaviors"}</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {directBehaviorRecords.filter(r => r.behavior_type?.category === 'negative').slice(0, 5).map((record) => (
                    <div key={record.id} className="flex items-center justify-between p-2 bg-red-50/80 dark:bg-red-900/20 rounded-lg">
                      <div>
                        <div className="font-medium text-sm text-foreground">{translateBehaviorName(record.behavior_type?.name)}</div>
                        <div className="text-xs text-muted-foreground">
                          {format(new Date(record.incident_date), 'dd/MM/yyyy')}
                        </div>
                      </div>
                      <div className="text-red-700 dark:text-red-400 font-bold text-sm">
                        {record.score_applied}
                      </div>
                    </div>
                  ))}
                  {directNegativeCount > 5 && (
                    <div className="text-xs text-muted-foreground text-center">
                      και {directNegativeCount - 5} ακόμα...
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Νέο Σύστημα - Επεξήγηση Κριτηρίων */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
            <BarChart3 className="h-5 w-5" />
            {t("evaluationCriteriaNewSystem") || "Evaluation Criteria - New System"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground space-y-3">
            <div className="bg-blue-50/80 dark:bg-blue-900/20 p-3 rounded-lg">
              <h4 className="font-medium text-blue-800 dark:text-blue-300 mb-2">🎯 {t("basePointSystem") || "Base Point System"}</h4>
              <p>{t("baseScoreFromOVITEvaluation") || "Base score comes from OVIT evaluation (0-80 points). Without OVIT evaluation, base score is 0 points. Additional points are added/subtracted based on:"}</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="space-y-2">
                <h5 className="font-medium text-green-700 dark:text-green-400">➕ {t("positivePoints") || "Positive Points:"}:</h5>
                <ul className="text-xs space-y-1 ml-4">
                  <li>• +2 {t("pointsPerPatrolActivity") || "points per patrol"}</li>
                  <li>• +5 {t("pointsPerEmergencyReport") || "points per emergency report"}</li> 
                  <li>• +3 {t("pointsForTimelyArrival") || "points for timely arrival"}</li>
                  <li>• +1 {t("pointForPresenceWithin15min") || "point for presence within 15min"}</li>
                  <li>• +3-15 {t("pointsForPositiveSupervisorReports") || "points for positive supervisor reports"}</li>
                  <li>• {t("pointsFromBehaviorTracking") || "Points from behavior tracking"}</li>
                </ul>
              </div>
              
              <div className="space-y-2">
                <h5 className="font-medium text-red-700 dark:text-red-400">➖ {t("negativePoints") || "Negative Points:"}:</h5>
                <ul className="text-xs space-y-1 ml-4">
                  <li>• -10 {t("pointsForAbsence") || "points for absence"}</li>
                  <li>• -2 {t("pointsForDelay15to30min") || "points for delay 15-30min"}</li>
                  <li>• -5 {t("pointsForDelayOver30min") || "points for delay >30min"}</li>
                  <li>• -5 {t("to") || "to"} -20 {t("pointsForNegativeReports") || "points for negative reports"}</li>
                  <li>• {t("negativePointsFromBehaviorRecords") || "Negative points from behavior records"}</li>
                  <li>• {t("negativePointsFromClientEvaluations") || "Negative points from client evaluations"}</li>
                </ul>
              </div>
            </div>

            <div className="bg-muted/50 p-3 rounded-lg mt-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-xs">
                <div className="flex justify-between">
                  <span>{t('patrols') || 'Περιπολίες'}:</span>
                  <span className={performanceResult?.details?.patrol_activities > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}>
                    {performanceResult?.details?.patrol_activities || 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>{t('emergency') || 'Έκτακτες'}:</span>
                  <span className={performanceResult?.details?.emergency_reports > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}>
                    {performanceResult?.details?.emergency_reports || 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Supervisors:</span>
                  <span className={supervisorReports.length > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}>
                    {supervisorReports.length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>{t('clients') || 'Πελάτες'}:</span>
                  <span className={performanceResult?.details?.client_evaluations_count > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}>
                    {performanceResult?.details?.client_evaluations_count || 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>{t('shifts') || 'Βάρδιες'}:</span>
                  <span className={performanceResult?.details?.total_shifts > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}>
                    {performanceResult?.details?.total_shifts || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Source Modal */}
      <PerformanceSourceModal
        isOpen={!!selectedMetric}
        onClose={() => setSelectedMetric(null)}
        metricType={selectedMetric || ''}
        guardId={guardId}
        data={{
          patrols,
          emergencyReports, 
          supervisorReports, 
          clientEvaluations,
          behaviorRecords: directBehaviorRecords, // Use directBehaviorRecords which has the actual data
          clockRecords
        }}
      />
    </div>
  );
};